import { useEffect, useState } from 'react';
import { authAPI, customerSupportAPI } from '../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import Pagination from '../../../common/components/Pagination';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import UnregisterProduct from './UnregisterProduct';
import RegisterProduct from './RegisterProduct';
import { getToken } from '../../../../utils/localStorage';
import useGetProductImage from '../../../common/hooks/useGetProductImage';
import { getProductImage } from '../../../../utils/commonFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { setAddDeviceStatus } from '../../../../store/deviceSlice';
import TableSkeleton from '../../../common/skeletons/TableSkeleton';
import Select from 'react-select';
import { reactDropDownStyles } from '../../../../utils/constants';
import { openNotification } from '../../../../store/commonSlice';
import WebSupport from '../WebSupport';
interface ProductsProps {
	callbackProducts: (payload: any) => void;
}
const options = [
	{ value: 'firmware', label: 'Firmware' },
	{ value: 'usermanual', label: 'User Manual' },
	{ value: 'support', label: 'Support' },
	{ value: 'techspecs', label: 'Device Specs' },
	{ value: 'unregister', label: 'Unregister' }
];
const Products = ({ callbackProducts }: ProductsProps) => {
	const [isUnregisterDeviceInfoModalOpen, setIsUnregisterDeviceInfoModalOpen] = useState(false);
	const [isProductRegisterInfoModalOpen, setIsProductRegisterInfoModalOpen] = useState(false);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [registerProducts, setRegisterProducts] = useState<any>([]);
	const [registerProductsItem, setRegisterProductsItem] = useState({});
	const [serialNumber, setSerialNumber] = useState('');
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [requestRedwolfService] = useRequestRedwolfService();
	const [imageDetails, setImageDetails] = useState([]);
	const [fetchProductImage] = useGetProductImage();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Products' });

	const { isDeviceAdded } = useSelector((state: any) => state.device);
	const [isModalDeviceModalNumber, setModalDeviceModalNumber] = useState<string | null>(null);
	const [isModalDeviceSerialNumber, setModalDeviceSerialNumber] = useState<string | null>(null);
	const [modalVisible, setModalVisible] = useState(false);
	const dispatch = useDispatch();
	useEffect(() => {
		getRegisterProductList();
	}, []);

	useEffect(() => {
		if (isDeviceAdded) {
			getRegisterProductList();
		}
	}, [isDeviceAdded]);

	const getRegisterProductList = () => {
		setRegisterProducts([]);
		setIsSkeletonLoading(true);
		requestRedwolfService(authAPI.getRegisteredProducts(token))
			.then((sdkResponse: any) => {
				if (isDeviceAdded) {
					dispatch(setAddDeviceStatus(false));
				}
				if (sdkResponse.constructor.$metadata$.simpleName === 'Success') {
					const productList = sdkResponse.data.products.toArray();
					const productIds: any = [];

					productList.forEach((product: any) => {
						productIds.push(product.model);
					});

					if (productIds && productIds.length > 0) {
						fetchProductImage(productIds)
							.then((response: any) => {
								if (response) {
									setImageDetails(response);

									const exitingRegisteredProducts = productList.map((v: any) => ({
										...v,
										isActive: false,
										selectedOptionValue: 'Info'
									}));
									setRegisterProducts(exitingRegisteredProducts);
									setIsSkeletonLoading(false);
								} else {
									setIsSkeletonLoading(false);
								}

								transaction && transaction.end();
							})
							.catch((err: any) => {
								const exitingRegisteredProducts = productList.map((v: any) => ({
									...v,
									isActive: false,
									selectedOptionValue: 'Info'
								}));
								setRegisterProducts(exitingRegisteredProducts);
								transaction && transaction.end();
								setIsSkeletonLoading(false);
								Sentry.captureException(err);
							});
					} else {
						const exitingRegisteredProducts = productList.map((v: any) => ({
							...v,
							isActive: false,
							selectedOptionValue: 'Info'
						}));
						setRegisterProducts(exitingRegisteredProducts);
						transaction && transaction.end();
						setIsSkeletonLoading(false);
					}
				} else {
					throw new Error(sdkResponse);
				}
			})
			.catch((err: any) => {
				setIsSkeletonLoading(false);
				dispatch(setAddDeviceStatus(false));
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleUnRegisterProduct = (event: any, productserialNumber: any) => {
		setIsUnregisterDeviceInfoModalOpen(true);
		setSerialNumber(productserialNumber);
	};

	const handlePagination = (currentPage: any) => {
		setCurrentPageNumber(parseInt(currentPage));
	};

	const handleTypeSelect = (event: any, card: any, index: any) => {
		const indexVal = registerProducts.findIndex(
			(obj: any) => obj.serialNumber === card.serialNumber
		);
		const existingProductsList = [...registerProducts];
		existingProductsList[indexVal]['selectedOptionValue'] = event.value;
		setRegisterProducts(existingProductsList);
		setRegisterProductsItem(card);
		const modelNumber = card.model;
		if (event.value === 'support') {
			setModalVisible(true);
			setModalDeviceModalNumber(card.model);
			setModalDeviceSerialNumber(card.serialNumber);
		} else if (event.value === 'firmware') {
			getProductAssets(modelNumber, 'firmware');
		} else if (event.value === 'techspecs') {
			getProductAssets(modelNumber, 'techspecs');
		} else if (event.value === 'usermanual') {
			getProductAssets(modelNumber, 'usermanual');
		} else if (event.value === 'unregister') {
			handleUnRegisterProduct(event, card.serialNumber);
		}
	};

	const getProductAssets = (modelNumberRes: any, assetTypeString: any) => {
		requestRedwolfService(customerSupportAPI.getProductAssets(modelNumberRes))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					let productAsset: any = [];
					if (response && response.data) {
						productAsset = response.data.toArray();
					}
					if (productAsset.length === 0) {
						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: 'Asset types are not available for this product.'
							})
						);
					}
					if (productAsset.length > 0) {
						let attributeKey: any = '';
						let labelKey: any = '';
						if (assetTypeString === 'firmware') {
							attributeKey = 'product-firmware-page';
							labelKey = 'Firmware';
						} else if (assetTypeString === 'usermanual') {
							attributeKey = 'user-manual';
							labelKey = 'User Manual';
						} else if (assetTypeString === 'techspecs') {
							attributeKey = 'product-support-page';
							labelKey = 'Device Specs';
						}
						const found = productAsset.find((obj: any) => {
							return obj.assetType === attributeKey;
						});
						if (found) {
							window.open(found.url, '_blank');
						} else {
							dispatch(
								openNotification({
									type: 'ERROR',
									heading: 'Error',
									body: `${labelKey} not available for this product`
								})
							);
						}
					}
				} else {
					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: 'Unable to process your request. Please try again later.'
						})
					);
				}
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
			});
	};

	const onDropdownKeyDown = (event: any, product: any, index: any) => {
		if (event.code === 'Enter') {
			setRegisterProducts(
				registerProducts.map((l: { serialNumber: any }) =>
					l.serialNumber == product.serialNumber ? { ...l, isActive: true } : l
				)
			);
		}
	};

	const onDropdownMenuOpen = (product: any) => () => {
		const indexVal = registerProducts.findIndex(
			(obj: any) => obj.serialNumber === product.serialNumber
		);
		const existingProductsList = [...registerProducts];
		existingProductsList[indexVal]['isActive'] = true;
		setRegisterProducts(existingProductsList);
	};

	const onDropdownMenuClose = (product: any) => () => {
		const indexVal = registerProducts.findIndex(
			(obj: any) => obj.serialNumber === product.serialNumber
		);
		const existingProductsList = [...registerProducts];
		existingProductsList[indexVal]['isActive'] = false;
		setRegisterProducts(existingProductsList);
	};

	const renderImage = (modelName: string) => {
		const defaultProductImage = 'images/vizio-logo-print.svg';
		const imageList: any = imageDetails.find((product: any) => product.id === modelName);
		const imageURL = imageList && getProductImage(imageList.images);
		return imageURL ? imageURL : defaultProductImage;
	};

	const paginationPerPage = 5;
	const indexOfLastItem = currentPageNumber * paginationPerPage;
	const indexOfFirstItem = indexOfLastItem - paginationPerPage;
	const currentRegisteredList = registerProducts.slice(indexOfFirstItem, indexOfLastItem);
	let optionsFilter: any = options;
	callbackProducts(registerProducts.length);
	const closeModal = () => {
		setModalVisible(false);
	};
	return (
		<>
			<div className="subs-head">
				<h2 className="ac-title" id="registred-product">
					Registered Products
				</h2>
				<div className="subs-btn">
					<button
						type="submit"
						className="primary-button-small link-device-btn"
						aria-label="Register Products"
						onClick={() => setIsProductRegisterInfoModalOpen(true)}>
						register
					</button>
				</div>
			</div>

			{registerProducts && registerProducts.length > 0 ? (
				<div id="reg-products-list">
					<div className="subs-table">
						<table className="table subscrip-table">
							<thead>
								<tr>
									<th>Product Name</th>
									<th>Model</th>
									<th>Serial Number</th>
									<th>More</th>
								</tr>
							</thead>
							<tbody>
								{currentRegisteredList &&
									currentRegisteredList.map((product: any, index: number) => {
										if (product.productType === 'Sound Bar') {
											optionsFilter = options.filter((item) => item.value !== 'firmware');
										} else {
											optionsFilter = options;
										}
										return (
											<tr key={index}>
												<td className="app-td">
													<div className="icon-title">
														<div className="sapp-icon brand-img">
															<img src={renderImage(product.model)} alt="product" />
														</div>
														<div className="sapp-title">
															<h3 id={`regproduct-${product.serialNumber}`}>{product.product}</h3>
															<span className="mob sapp-modal-no">
																<span className="modal-no">{product.model}</span>
																<span className="serial-no">{product.serialNumber}</span>
															</span>
														</div>
													</div>
												</td>
												<td className="app-td desk">
													<span className="modal-no">{product.model}</span>
												</td>
												<td className="app-td desk">
													<span className="serial-no">{product.serialNumber}</span>
												</td>
												<td className="app-td drop_down">
													<Select
														styles={reactDropDownStyles}
														isSearchable={false}
														id={`option-${index}`}
														getOptionLabel={(option) => option.label}
														options={optionsFilter}
														onChange={(event) => handleTypeSelect(event, product, index)}
														onKeyDown={(event) => onDropdownKeyDown(event, product, index)}
														onMenuOpen={onDropdownMenuOpen(product)}
														onMenuClose={onDropdownMenuClose(product)}
														menuIsOpen={product.isActive}
														placeholder="Info"
														aria-labelledby={product.selectedOptionValue}
														value={options.filter(
															({ value }) => value === product.selectedOptionValue
														)}
													/>
												</td>
											</tr>
										);
									})}
							</tbody>
						</table>
						{registerProducts && registerProducts.length > 5 && (
							<div className="table-navigation">
								<Pagination
									totalNumberOfPages={registerProducts.length}
									currentPage={currentPageNumber}
									paginationPerPage={paginationPerPage}
									onPaginationChange={handlePagination}
								/>
							</div>
						)}
					</div>
				</div>
			) : isSkeletonLoading ? (
				<TableSkeleton />
			) : (
				<div id="register-products">
					<p id="empty-reg-products">
						Register your TVs and Sound Bars with your VIZIO Account to view device information,
						receive personalized support, and use the VIZIO Mobile App as a remote.
					</p>
				</div>
			)}
			{isUnregisterDeviceInfoModalOpen && (
				<UnregisterProduct
					getRegisterProductList={getRegisterProductList}
					serialNumber={serialNumber}
					isUnregisterDeviceInfoModalOpen={isUnregisterDeviceInfoModalOpen}
					handleModalUnregisterClose={() => setIsUnregisterDeviceInfoModalOpen(false)}
				/>
			)}

			{isProductRegisterInfoModalOpen && (
				<RegisterProduct
					isProductRegisterInfoModalOpen={isProductRegisterInfoModalOpen}
					getRegisterProductList={getRegisterProductList}
					handleModalRegisterClose={() => setIsProductRegisterInfoModalOpen(false)}
				/>
			)}
			<WebSupport
				modalVisible={modalVisible}
				closeModal={closeModal}
				deviceData={registerProductsItem}
				isModalDeviceModalNumber={isModalDeviceModalNumber}
				isModalDeviceSerialNumber={isModalDeviceSerialNumber}
			/>
		</>
	);
};

export default Products;
